import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { supabase } from "../lib/supabase";

const inputCls =
  "w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-4 py-3 text-sm text-[#404040] placeholder-[#9B9B8F] focus:outline-none focus:border-[#B09060] focus:ring-1 focus:ring-[#B09060]/40 transition-colors";
const labelCls = "block text-xs uppercase tracking-[0.2em] font-semibold text-[#8F7648] mb-1.5";

// Supabase recovery bağlantısı iki biçimde gelebilir:
//   PKCE:   /reset-password?code=...
//   Legacy: /reset-password#access_token=...&type=recovery
// Her ikisini de işler; PASSWORD_RECOVERY olayıyla formu açar.
export default function ResetPassword() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | ready | invalid
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    let subscription;

    async function initialise() {
      // Listener exchange'den önce kurulmalı; callback içinde await kullanılmaz.
      const { data } = supabase.auth.onAuthStateChange((event) => {
        if (event === "PASSWORD_RECOVERY" && alive) setStatus("ready");
      });
      subscription = data.subscription;

      const code = new URLSearchParams(window.location.search).get("code");
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (!alive) return;
        if (error) {
          setStatus("invalid");
          return;
        }
        setStatus("ready");
        window.history.replaceState({}, document.title, "/reset-password");
        return;
      }

      // Legacy hash akışı: supabase-js tokenları kendisi işler.
      const isRecoveryHash =
        new URLSearchParams(window.location.hash.replace(/^#/, "")).get("type") === "recovery";
      const { data: sessionData } = await supabase.auth.getSession();
      if (!alive) return;
      if (isRecoveryHash && sessionData.session) {
        setStatus("ready");
      } else {
        // Ne code ne recovery hash var: bağlantı eksik/geçersiz
        setTimeout(() => {
          setStatus((s) => (s === "checking" ? "invalid" : s));
        }, 1500);
      }
    }

    initialise().catch(() => alive && setStatus("invalid"));
    return () => {
      alive = false;
      subscription?.unsubscribe();
    };
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Yeni şifre en az 8 karakter olmalıdır.");
      return;
    }
    if (password !== confirm) {
      toast.error("Girdiğiniz şifreler eşleşmiyor.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      toast.error(`Şifre güncellenemedi: ${error.message}`);
      return;
    }
    await supabase.auth.signOut();
    toast.success("Şifreniz güncellendi. Giriş sayfasına yönlendiriliyorsunuz.");
    setTimeout(() => navigate("/admin/giris", { replace: true }), 1500);
  }

  return (
    <div className="min-h-screen bg-[#FAFBF6] flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm bg-white border border-[#B09060]/20 shadow-[0_4px_20px_rgba(176,144,96,0.08)] p-8">
        <div className="flex flex-col items-center text-center">
          <img src="/assets/logo-monogram.svg" alt="VIA PRIME" className="w-12 h-12 object-contain" />
          <h1 className="font-serif-display text-2xl font-medium text-[#404040] mt-4">
            Yeni Şifre Belirleme
          </h1>
        </div>

        {status === "checking" && (
          <p className="text-sm text-[#6B6B6B] text-center mt-6" data-testid="reset-checking">
            Bağlantınız doğrulanıyor...
          </p>
        )}

        {status === "invalid" && (
          <div className="text-center mt-6" data-testid="reset-invalid">
            <p className="text-sm text-red-700 leading-relaxed">
              Bu bağlantı geçersiz veya süresi dolmuş. Lütfen yeni bir şifre
              sıfırlama bağlantısı isteyin.
            </p>
            <Link
              to="/admin/giris"
              data-testid="reset-back-to-login"
              className="inline-block mt-5 text-xs font-semibold uppercase tracking-[0.15em] text-[#8F7648] hover:text-[#B09060] transition-colors"
            >
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {status === "ready" && (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
            <div>
              <label className={labelCls} htmlFor="new-password">Yeni Şifre</label>
              <input
                id="new-password"
                data-testid="reset-password-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="En az 8 karakter"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="confirm-password">Yeni Şifre (Tekrar)</label>
              <input
                id="confirm-password"
                data-testid="reset-confirm-input"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Şifrenizi tekrar girin"
                className={inputCls}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit-button"
              className="w-full py-3.5 rounded-sm bg-[#B09060] text-[#FAFBF6] font-semibold text-xs uppercase tracking-[0.15em] hover:bg-[#8F7648] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <KeyRound className="w-4 h-4" strokeWidth={1.5} />
              {busy ? "Güncelleniyor..." : "Şifremi Güncelle"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
